/**
 * Injected data resolvers for commerce blocks.
 *
 * The site-blocks package ships no data layer of its own: blocks describe
 * what they need (a product handle, a collection), and the host application
 * decides where the data comes from. The storefront registers server-side
 * resolvers (wrappers over @lib/data) before rendering a published page; the
 * admin editor never registers any and blocks fall back to placeholders.
 *
 * The registry is module-level mutable state. In the storefront it is set
 * once per request inside the server renderer; in the editor it stays empty
 * for the whole process lifetime.
 */

/**
 * JSON-serializable product snapshot handed to blocks. Prices come
 * preformatted from the resolver, so blocks know nothing about locales or
 * currency conversion.
 */
export type SiteProduct = {
  id: string
  title: string
  handle: string
  thumbnail: string | null
  /** Formatted current price, e.g. "1 234 ₽". Empty when no price is found. */
  price: string
  /** Plain-text description, already truncated by the resolver. */
  description: string
  /** Tenant-relative product URL, e.g. "/products/shirt". */
  href: string
}

export type ShowcaseQuery = {
  collectionHandle: string
  /** Comma separated tags; empty = ignore */
  tags: string
  limit: number
}

/**
 * Everything the host must provide for commerce blocks to resolve data.
 * `locale` is the storefront locale (e.g. "ru") used for price formatting.
 */
export type SiteResolvers = {
  productByHandle: (
    handle: string
  ) => Promise<SiteProduct | null>
  showcaseProducts: (query: ShowcaseQuery) => Promise<SiteProduct[]>
}

let resolvers: SiteResolvers | null = null

/**
 * Registers the host's data resolvers. Call once per request in the server
 * renderer before resolving page data. Passing null clears the registry.
 */
export function registerResolvers(next: SiteResolvers | null): void {
  resolvers = next
}

/**
 * Returns the registered resolvers, or null when running outside the
 * storefront (admin editor) — blocks then render placeholders.
 */
export function getResolvers(): SiteResolvers | null {
  return resolvers
}
